import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import type { LoginResponse, ChatResponse, AdminStats, AdminUser, BudgetSettings } from '@/types';

const API_BASE_URL = 'https://api.sheehanai.com';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          useAuthStore.getState().logout();
        }
        return Promise.reject(error);
      }
    );
  }

  async login(pin: string): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/api/auth/login', { pin });
    return response.data;
  }

  async sendMessage(
    message: string,
    conversationId: string | undefined,
    endpoint: string,
    mode?: string
  ): Promise<ChatResponse> {
    const body: Record<string, unknown> = { message };
    if (conversationId) body.conversationId = conversationId;
    if (mode) body.mode = mode;

    const response = await this.client.post<ChatResponse>(endpoint, body);
    return response.data;
  }

  async planItOut(conversationId: string): Promise<ChatResponse> {
    const response = await this.client.post<ChatResponse>('/api/venture/plan-it-out', {
      conversationId,
    });
    return response.data;
  }

  async advancePlan(conversationId: string): Promise<ChatResponse> {
    const response = await this.client.post<ChatResponse>('/api/plan/advance', {
      conversationId,
    });
    return response.data;
  }

  async getAdminDashboard(): Promise<AdminStats> {
    const response = await this.client.get<AdminStats>('/api/admin/dashboard');
    return response.data;
  }

  async getUsers(): Promise<AdminUser[]> {
    const response = await this.client.get<AdminUser[]>('/api/admin/users');
    return response.data;
  }

  async createUser(user: Omit<AdminUser, 'id'>): Promise<AdminUser> {
    const response = await this.client.post<AdminUser>('/api/admin/users', user);
    return response.data;
  }

  async updateUser(id: number, user: Partial<AdminUser>): Promise<AdminUser> {
    const response = await this.client.patch<AdminUser>(`/api/admin/users/${id}`, user);
    return response.data;
  }

  async getBudgetSettings(): Promise<BudgetSettings> {
    const response = await this.client.get<BudgetSettings>('/api/admin/budget');
    return response.data;
  }

  async updateBudgetSettings(settings: BudgetSettings): Promise<BudgetSettings> {
    const response = await this.client.patch<BudgetSettings>('/api/admin/budget', settings);
    return response.data;
  }

  async healthCheck(): Promise<{ status: string }> {
    const response = await this.client.get<{ status: string }>('/api/health');
    return response.data;
  }
}

export const api = new ApiService();
